/* @flow */

import { compose } from 'ramda';
import * as React from 'react';
import { intlShape, injectIntl } from 'react-intl';
import { createUseStyles } from 'react-jss';
import {
  getUserFullName,
  getUserPosition,
  getUserCompany,
  getUserFacebookLink,
  getUserLinkedInLink,
  getUserTwitterUsername,
  getUserWebsite,
  getUserGithubLink,
} from '@e180/domain/lib/user/util';

import styles from './UserIntro.style';
import UserDetailsSection from './UserDetailsSection';
import UserSocial from './UserSocial';
import UserStats from './UserStats';
import UserAvatar, {
  xlarge,
} from 'src/shared/components/domain/user/avatar/UserAvatar';
import messages from 'src/shared/domain/user/l10n/userL10n';

import type { User } from '@e180/domain/lib/user/type';

type Props = {
  user: User,
  hideStats?: boolean,
  hideSocial?: boolean,
  hideConnectedBadge?: boolean,
  useDefaultAvatar?: boolean,
  // Self passed
  // $FlowIssue
  intl: intlShape,
};

const useStyles = createUseStyles(styles);

const UserIntro = ({
  user,
  hideStats,
  hideSocial,
  hideConnectedBadge,
  useDefaultAvatar,
  intl,
}: Props): React.Node => {
  const { locale } = intl;
  const name = getUserFullName(user);
  const position = getUserPosition(user, locale);
  const company = getUserCompany(user, locale);
  const title =
    position && company
      ? intl.formatMessage(messages.fullPosition, { position, company })
      : position || company;

  /*
   |----------------------------------------------------------------------------
   | Classes
   |----------------------------------------------------------------------------
   */

  const classes: Object = useStyles();

  /*
   |----------------------------------------------------------------------------
   | Elements
   |----------------------------------------------------------------------------
   */

  const titleElt = title && <span className={classes.title}>{title}</span>;
  const statsElt = !hideStats && <UserStats user={user} />;
  const socialElt =
    !hideSocial &&
    (getUserFacebookLink(user) ||
      getUserLinkedInLink(user) ||
      getUserTwitterUsername(user) ||
      getUserWebsite(user) ||
      getUserGithubLink(user)) ? (
      <div className={classes.social}>
        <UserSocial user={user} />
      </div>
    ) : null;

  return (
    <UserDetailsSection>
      <div className={classes.avatar}>
        <UserAvatar
          user={user}
          variant={xlarge}
          showBadge={!hideConnectedBadge}
          useDefaultAvatar={useDefaultAvatar}
        />
      </div>
      <p className={classes.nameCtn}>
        <span className={classes.name}>{name}</span>
        {titleElt}
      </p>
      {statsElt}
      {socialElt}
    </UserDetailsSection>
  );
};

export default compose(React.memo, injectIntl)(UserIntro);
